import { Dispatch, SetStateAction } from 'react';
import { useRouter } from 'next/navigation';
import {
  DatadogErrorLevels,
  NEXT_ROUTES,
} from '@bts-web/utils-next-infrastructure';
import { getAppConfig } from '@bts-web/core-app-config';
import { TradeInputErrorType } from '../../useTradeInput';
import { createSavingsPlanAction } from '../actions/createSavingsPlan.action';
import { datadogErrorHelper } from '../../../../../../common';
import { setWindowHistoryState } from '@bts-web/utils-functions';
import { VERIFY_LINK_KEY } from '../../../../constants';
import { IUseTradeForm } from '../useTradeForm';
import { processDeepLink } from '../../../../utils/processDeepLink';
import { SavingsPlanFrequency } from '@bts-web/data-layer/server';
import { setInputValueToLocalStorage } from '../../../../utils/localStorageInputValueUtils';
import { setLegacyPendingCreateSavingsFlowKeyAction } from '../../../../legacy-freeze/legacyPendingTradeFlowCookie.actions';
import { dateScalarFromValidYmdString } from '@bts-web/utils-formatting';

function getLegacySavingsAcceptTriggerPath(assetId: string): string {
  return `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId)}/buy/savings`;
}

async function setLegacyCreateSavingsCookie(assetId: string): Promise<boolean> {
  try {
    await setLegacyPendingCreateSavingsFlowKeyAction();

    return true;
  } catch (error) {
    datadogErrorHelper({
      errorMessage: `[CREATE SAVINGS PLAN] failed to set savings cookie; assetId=${assetId}; error=${error}`,
      errorSeverity: DatadogErrorLevels.HIGH,
    });

    return false;
  }
}

/**
 * Submits a new savings plan from trade `FormData`. The recurring date field must be a valid
 * `YYYY-MM-DD` (e.g. HTML date input); `dateScalarFromValidYmdString` (`@bts-web/utils-formatting`) is the
 * trusted boundary that turns that wire/form string into a `DateScalar` for `createSavingsPlanAction`.
 */
export const createSavingsPlan = async (
  formData: FormData,
  data: {
    assetDetails: IUseTradeForm['assetDetails'];
  },
  handlers: {
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setErrors: Dispatch<SetStateAction<TradeInputErrorType>>;
    processErrors: (gqlResponseErrors?: string[]) => Promise<void>;
    router: ReturnType<typeof useRouter>;
  },
) => {
  const formattedAmount = (formData.get('amount') as string).replace(/,/g, '.');

  const frequency =
    (formData.get('savingsPlanFrequency') as SavingsPlanFrequency) ?? 'MONTHLY';

  setInputValueToLocalStorage({
    value: formattedAmount,
    assetId: data.assetDetails.id,
  });

  try {
    const dateInput = formData.get('savingsPlanRecurringDate');

    if (typeof dateInput !== 'string') {
      throw new Error('Missing savings plan recurring date');
    }

    const date = dateScalarFromValidYmdString(dateInput);

    const response = await createSavingsPlanAction({
      assetId: data.assetDetails.id,
      amount: formattedAmount,
      date,
      frequency,
    });

    handlers.setIsLoading(false);

    if (response.errors) {
      handlers.processErrors(response.errors);

      datadogErrorHelper({
        errorMessage: `[CREATE SAVINGS PLAN] GraphQL errors; assetId=${data.assetDetails.id}; errors=${JSON.stringify(response.errors)}`,
        errorSeverity: DatadogErrorLevels.HIGH,
      });

      return;
    }

    if (!response.data || !response.data.createSavingsPlan) {
      throw new Error('server failure');
    }

    const deepLink =
      'deepLink' in response.data.createSavingsPlan
        ? response.data.createSavingsPlan.deepLink
        : '';

    const processedDeepLink = processDeepLink(deepLink);

    if (
      response.data.createSavingsPlan.__typename ===
      'SavingsPlanSignatureNeeded'
    ) {
      const useLegacy = !!getAppConfig().useLegacyOperationUrls;

      if (useLegacy) {
        const isLegacyCookieReady = await setLegacyCreateSavingsCookie(
          data.assetDetails.id,
        );

        if (!isLegacyCookieReady) {
          handlers.processErrors(['generic_monitored_error_description']);

          return;
        }
      }

      const verifyLink = processedDeepLink;

      if (verifyLink) {
        setWindowHistoryState({
          newHistoryState: {
            key: VERIFY_LINK_KEY,
            value: verifyLink,
          },
          searchParams: useLegacy ? '' : window.location.search,
          pathname: useLegacy
            ? getLegacySavingsAcceptTriggerPath(data.assetDetails.id)
            : window.location.pathname,
        });

        return;
      }

      datadogErrorHelper({
        errorMessage: `[ERR][CREATE SAVINGS PLAN] missing 2FA deepLink`,
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });

      handlers.processErrors(['generic_monitored_error_description']);

      return;
    }

    if (response.data.createSavingsPlan.__typename === 'SavingsPlan') {
      handlers.router.push(
        `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', data.assetDetails.id)}/savings/${response.data.createSavingsPlan.id}`,
      );

      return;
    }

    const typename = response.data.createSavingsPlan.__typename;

    datadogErrorHelper({
      errorMessage: `[CREATE SAVINGS PLAN] Unexpected __typename; assetId=${data.assetDetails.id}; __typename=${typename}`,
      errorSeverity: DatadogErrorLevels.HIGH,
    });

    handlers.processErrors(['generic_monitored_error_description']);
  } catch (error) {
    handlers.setIsLoading(false);

    handlers.processErrors(['generic_monitored_error_description']);

    datadogErrorHelper({
      errorMessage: `[ERR][CREATE SAVINGS PLAN] ${error}`,
      errorSeverity: DatadogErrorLevels.CRITICAL,
    });
  }
};
